/**
 * Role permissions — the read-only "what does {role} grant" overlay. Opened
 * from a role chip's trailing affordance in <select-role>, for ANY role in
 * the catalog, selected or not — previewing is how you decide which chip to
 * click, and stays decoupled from committing to a selection.
 *
 * Renders the identical <permission-picker> tree the role editor uses,
 * readonly. No commit/selection control lives here — this surface only
 * VIEWS; choosing a role happens on the chip itself, back in <select-role>.
 *
 * Takes a roleId rather than a Role snapshot so the overlay always reads the
 * live catalog (rolesState) rather than a value closed over at the moment
 * the trigger rendered.
 *
 * Header is one inline text row — name · [Built-in ·] · N permissions — no
 * badge pill, no stacked second row.
 */
import { overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { rolesState } from "../members.state.ts";
import "./permission-picker.view.ts";

@schmancyElement("role-permissions")
export class RolePermissions extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) roleId = "";

	private close(): void {
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	override render(): TemplateResult {
		const role = rolesState.value.find((r) => r.id === this.roleId);
		if (!role) return html``;
		const granted = new Set(role.permissions);
		const grantedLabel =
			role.permissions.length === 1
				? this.t("admin.members.common.permissionsCountOne", {
						count: role.permissions.length,
					})
				: this.t("admin.members.common.permissionsCountOther", {
						count: role.permissions.length,
					});

		return html`
			<div class="flex flex-col w-96 max-w-[90vw] max-h-[70vh]">
				<div class="flex items-center gap-3 px-5 pt-5 pb-3">
					<div class="min-w-0 flex-1 flex items-center gap-1.5">
						<schmancy-typography type="title" token="md" class="truncate min-w-0">${role.name}</schmancy-typography>
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant whitespace-nowrap shrink-0">
							·
							${when(
								role.isBuiltIn,
								() => html`
									<schmancy-icon class="text-sm text-surface-onVariant align-text-bottom" aria-hidden="true">lock</schmancy-icon>
									${this.t("admin.members.common.builtInLabel")} ·
								`,
							)}
							${grantedLabel}
						</schmancy-typography>
					</div>
					<schmancy-icon-button icon="close" variant="text" aria-label=${this.t("admin.common.close")} @click=${() => this.close()}></schmancy-icon-button>
				</div>

				<div ${overflowWithin()} class="px-5 py-2 flex-1 min-h-0 flex flex-col gap-4">
					<permission-picker readonly .selected=${granted}></permission-picker>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"role-permissions": RolePermissions;
	}
}
